// DeleteConfirmationDialogContext.tsx
import React, { createContext, useContext, useState, ReactNode } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
} from "@mui/material";
import { theme } from "../theme";

interface DeleteDialogContextProps {
    openDeleteDialog: (onConfirm: () => void, message?: string) => void;
}

const DeleteDialogContext = createContext<DeleteDialogContextProps | undefined>(undefined);

export const useDeleteDialog = () => {
    const context = useContext(DeleteDialogContext);
    if (!context) {
        throw new Error("useDeleteDialog must be used within DeleteDialogProvider");
    }
    return context;
};

export const DeleteDialogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [open, setOpen] = useState(false);
    const [onConfirmCallback, setOnConfirmCallback] = useState<() => void>(() => { });
    const [message, setMessage] = useState("Are you sure you want to delete this item? This action cannot be undone.");

    const openDeleteDialog = (onConfirm: () => void, customMessage?: string) => {
        setOnConfirmCallback(() => onConfirm);
        setMessage(customMessage || "Are you sure you want to delete this item? This action cannot be undone.");
        setOpen(true);
    };

    const handleCancel = () => {
        setOpen(false);
    };

    const handleConfirm = async () => {
        try {
            await onConfirmCallback(); // ✅ await async deletes
        } finally {
            setOpen(false);
        }
    };

    return (
        <DeleteDialogContext.Provider value={{ openDeleteDialog }}>
            {children}

            <Dialog open={open} onClose={handleCancel}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>{message}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={handleCancel} sx={{ color: theme.palette.text.secondary }}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirm}
                        variant="contained"
                        sx={{
                            bgcolor: theme.palette.error.main,
                            '&:hover': {
                                bgcolor: theme.palette.error.dark,
                            },
                        }}>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </DeleteDialogContext.Provider>
    );
};
